import { createEvent, createStore, attach, sample } from "effector";
import "@apollo/client";
import { operationName } from "../lib/name.mjs";
import { optional } from "../lib/optional.mjs";
import { storify } from "../lib/storify.mjs";
import { createRemoteOperation } from "../remote_operation.mjs";
import { createQueryController } from "./controller.mjs";
function createQuery({
  client,
  document,
  context,
  name = operationName(document) || "unknown"
}) {
  const options = {
    query: document,
    returnPartialData: false,
    canonizeResults: true
  };
  const push = createEvent({ name: `${name}.push` });
  const $client = storify(client, { sid: `apollo.${name}.$client`, name: `${name}.client` });
  const $context = storify(context, { sid: `apollo.${name}.$context`, name: `${name}.context` });
  const $data = createStore(null, {
    name: `${name}.data`,
    sid: `apollo.${name}.$data`,
    skipVoid: false
  });
  const $error = createStore(null, {
    name: `${name}.error`,
    sid: `apollo.${name}.$error`,
    skipVoid: false
  });
  const handler = attach({
    source: { client: $client, context: $context },
    effect: ({ client: client2, context: context2 }, { variables, meta }) => {
      const fetchPolicy = meta.force ? "network-only" : "cache-first";
      return client2.query({ ...options, context: context2, variables, fetchPolicy }).then(({ data }) => data);
    }
  });
  const operation = createRemoteOperation({ name, handler });
  const controller = createQueryController({ operation, name });
  sample({
    clock: operation.finished.success,
    fn: ({ data }) => data,
    target: [$data, $error.reinit]
  });
  sample({ clock: push, target: $data });
  sample({
    clock: operation.finished.failure,
    fn: ({ error }) => error,
    target: [$error, $data.reinit]
  });
  sample({
    clock: operation.reset,
    target: [$data.reinit, $error.reinit]
  });
  return {
    ...operation,
    start: optional(controller.start),
    refresh: optional(controller.refresh),
    $data,
    $error,
    meta: { name, client: $client, document },
    __: { ...operation.__, push }
  };
}
export {
  createQuery
};
//# sourceMappingURL=query.mjs.map
